import {
  dossierUrlRegExp,
  libraryUrlRegExp,
  exampleSearchUrlRegExp,
} from '../constants/urls-regexp';
import EmbeddingType from './embeddingType';
// Modifies the given text to replace all strings in the array oldValues with the strings in newValues
const textReplaceAll = (text, oldValues, newValues) => {
  if (text) {
    let output = text;
    for (let i = 0; i < oldValues.length; i += 1) {
      output = output.replaceAll(oldValues[i], newValues[i]);
    }
    return output;
  }
  return text;
};
// Parse the example url used to display the example popup and return the exampleId uppercased
export const parseExampleIdFromSearchURI = (url) => {
  const urlMatched = url.match(exampleSearchUrlRegExp);
  let exampleId = urlMatched?.groups?.exampleId || '';
  exampleId = exampleId.toLowerCase();
  return exampleId;
};

function retrieveItemsFromMatchedUrl(url, urlMatched) {
  const baseServerUrl = urlMatched?.groups?.baseServerUrl || '';
  const libraryName = urlMatched?.groups?.libraryName || '';
  const projectID = urlMatched?.groups?.projectID || '';
  const dossierID = urlMatched?.groups?.dossierID || '';
  const applicationId = urlMatched?.groups?.configID || '';
  const libraryUrl = `${baseServerUrl}/${libraryName}`;
  const embeddinglibjsUrl = `${libraryUrl}/javascript/embeddinglib.js`;
  const dashboardUrl = url;

  return {
    dashboardUrl,
    baseServerUrl,
    libraryName,
    libraryUrl,
    embeddinglibjsUrl,
    projectID,
    dossierID,
    applicationId,
  };
}

// Parse the dashboard url and return the object {baseServerUrl, libraryName, and embeddinglibjsUrl}
export const parseDossierUrl = (url) => {
  const urlMatched = url.match(dossierUrlRegExp);
  return retrieveItemsFromMatchedUrl(url, urlMatched);
};

export const parseLibraryUrl = (url) => {
  const urlMatched = url.match(libraryUrlRegExp);
  return retrieveItemsFromMatchedUrl(url, urlMatched);
};

function handleQueryParams(url, queryParams, separator = '&') {
  let newUrl = url;
  const queryString = Object.keys(queryParams)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
    .join(separator);
  if (queryString) {
    newUrl = `${newUrl} + '?${queryString}'`;
  }
  return newUrl;
}

/**
 * Creates the html string given relativePath of the template and can replace the defaultUrl in template along with the embeddinglibjs url
 * Input: (relativePath: string required rest are optional, defaultUrl: string, url: string,)
 * Output: string
 */
const populateEnvInfo = (template, embeddingType, envSettingInfo, defaultEnvInfo) => {
  let templateStr = template;
  const defaultParsedUrl = parseDossierUrl(defaultEnvInfo.defaultUrl); // Returns an object containing {baseServerUrl, libraryName, and embeddinglibjsUrl} from given url
  const parsedUrl = parseDossierUrl(envSettingInfo.customUrl);
  // for these types we need handle the input of url
  if (EmbeddingType.Dashboard === embeddingType) {
    const currentStrings = [defaultParsedUrl.dashboardUrl, defaultParsedUrl.embeddinglibjsUrl];
    const newStrings = [parsedUrl.dashboardUrl, parsedUrl.embeddinglibjsUrl];

    // Outputs the content of the text file after replacing the currentStrings with newStrings
    templateStr = textReplaceAll(templateStr, currentStrings, newStrings);
  }
  // for these types we need to parse the url and replace the projectID and dossierID
  if (
    [
      EmbeddingType.Bot,
      EmbeddingType.Bot_Api,
      EmbeddingType.Visualization,
      EmbeddingType.NextGet_AI,
    ].includes(embeddingType)
  ) {
    const oldProjectId = defaultEnvInfo.projectId;
    const oldObjectId = defaultEnvInfo.objectId;
    const currentStrings = [
      oldProjectId,
      oldObjectId,
      defaultEnvInfo.defaultUrl,
      defaultParsedUrl.embeddinglibjsUrl,
    ];
    const newStrings = [
      parsedUrl.projectID,
      parsedUrl.dossierID,
      parsedUrl.libraryUrl,
      parsedUrl.embeddinglibjsUrl,
    ];
    templateStr = textReplaceAll(templateStr, currentStrings, newStrings);
  }
  // for these types we need handle the input of username and password
  if (
    [
      EmbeddingType.Bot_Api,
      EmbeddingType.Bot_Auto,
      EmbeddingType.Bot_AutoImage,
      EmbeddingType.NextGet_AI,
    ].includes(embeddingType)
  ) {
    const currentUsrPwd = [defaultEnvInfo.username, defaultEnvInfo.password];
    const newUsrPwd = [envSettingInfo.username, envSettingInfo.password];
    templateStr = textReplaceAll(templateStr, currentUsrPwd, newUsrPwd);
  }
  if (
    [EmbeddingType.Bot_Auto, EmbeddingType.Bot_AutoImage, EmbeddingType.Library].includes(
      embeddingType,
    )
  ) {
    const parsedLibUrl = parseLibraryUrl(envSettingInfo.customUrl);
    const defaultParsedLibUrl = parseLibraryUrl(defaultEnvInfo.defaultUrl);

    const currentStrings = [defaultParsedLibUrl.libraryUrl, defaultParsedLibUrl.embeddinglibjsUrl];
    const newStrings = [parsedLibUrl.libraryUrl, parsedLibUrl.embeddinglibjsUrl];

    // for Bot_Auto and Bot_AutoImage, we need to handle the query params
    if (embeddingType !== EmbeddingType.Library) {
      defaultEnvInfo.requestPhrases.forEach((currRequest) => {
        let newRequestUrl = currRequest;
        const queryParams = {};
        if (envSettingInfo.contentGroup) {
          queryParams.contentGroupIds = envSettingInfo.contentGroup;
        }
        if (parsedLibUrl.applicationId) {
          queryParams.applicationId = parsedLibUrl.applicationId;
        }
        newRequestUrl = handleQueryParams(currRequest, queryParams);
        currentStrings.push(currRequest);
        newStrings.push(newRequestUrl);
      });
    }
    templateStr = textReplaceAll(templateStr, currentStrings, newStrings);
  }
  return templateStr;
};

const duplicateCodeMap = {
  '//****BotAuto****': `
  // the status of the question
  const QUESTION_STATUS = {
    BOT_ROUTING: 'BOT_ROUTING',
    BOT_FOUND: 'BOT_FOUND',
    THINKING: 'THINKING',
    ANSWER_READY: 'ANSWER_READY',
    BOT_NOT_FOUND: 'BOT_NOT_FOUND',
  };

  // Pressing Enter key also sends message
  document.getElementById('userInput').addEventListener('keyup', function (event) {
    if (event.keyCode === 13) {
      // Enter key
      event.preventDefault();
      sendMessage();
    }
  });

  // when the user sends a message
  async function sendMessage() {
    let userInput = document.getElementById('userInput');
    let message = userInput.value;
    // clear the input box and disable it until we get responds
    userInput.value = '';

    if (message.trim() !== '') {
      // render the question message
      questionPromise = processQuestion(message);
    }
  }

  // render a question message
  function addQuestionMsg(msg) {
    var chatContainer = document.getElementById('chat-container');
    var messageElement = document.createElement('div');
    messageElement.classList.add('ask-message');
    // *** NEED TO SANITIZE THE INPUT TO PREVENT XSS ATTACKS! ***
    messageElement.textContent = DOMPurify.sanitize(msg);
    chatContainer.appendChild(messageElement);
    chatContainer.scrollTop = chatContainer.scrollHeight; // Scroll to bottom
  }

  function parseBotConfigs(answerData) {
    let info = answerData.split(':');
    return {
      projectId: info[0],
      objectId: info[1],
      questionId: info[2],
    };
  }

  // all the REST API calls
  let API = {
    async getSuggestions(authToken, serverUrl) {
      try {
        const getSuggestionsUrl = serverUrl + '/api/questions/suggestions';
        const response = await fetch(getSuggestionsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'X-MSTR-AuthToken': authToken,
          },
          body: JSON.stringify({
            count: 3, // replace with your suggestion items count
          }),
          credentials: 'include', // Including cookie
          mode: 'cors', // Setting as cors mode for cross origin
        });
        await checkApiStatus(response);
        return await response.json();
      } catch (err) {
        console.error(err);
      }
    },

    async postQuestion(msg) {
      try {
        const askQuestionUrl = serverUrl + '/api/questions'; // the contentGroupIds is optional here: ?contentGroupIds=75A330687C4E2520BA5819BB9476A387
        const question = JSON.stringify({
          text: msg,
          'answers.images': [{ width: 700, height: 400 }],
        });
        const response = await fetch(askQuestionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'X-MSTR-AuthToken': authToken,
            Prefer: 'respond-async',
          },
          body: question,
          credentials: 'include', // Including cookie
          mode: 'cors', // Setting as cors mode for cross origin
        });
        await checkApiStatus(response);
        const answer = await response.json();
        // return the answer id which contains the routed bot info
        return answer.id;
      } catch (err) {
        console.error(err);
      }
    },

    async getImage(questionId, imageId) {
      try {
        const getImageUrl = serverUrl + '/api/questions/' + questionId + '/answers/images/' + imageId;
        const response = await fetch(getImageUrl, {
          method: 'GET',
          headers: {
            'X-MSTR-AuthToken': authToken,
          },
          credentials: 'include', // Including cookie
          mode: 'cors', // Setting as cors mode for cross origin
        });
        await checkApiStatus(response);
        return await response.arrayBuffer();
      } catch (err) {
        console.error(err);
      }
    },

    async getQuestionAnswer(answerId) {
      try {
        const getAnswerUrl = serverUrl + '/api/questions/' + answerId;
        const answerRes = await fetch(getAnswerUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'X-MSTR-AuthToken': authToken,
            Prefer: 'respond-async',
          },
          credentials: 'include', // Including cookie
          mode: 'cors', // Setting as cors mode for cross origin
        });
        await checkApiStatus(answerRes);
        const content = await answerRes.json();
        return { content, status: answerRes.status };
      } catch (err) {
        console.error(err);
      }
    },

    async getBotConfig({ projectId, objectId }) {
      try {
        const askQuestionUrl = serverUrl + '/api/bots/' + objectId +'/configuration';
        let response;
        response = await fetch(askQuestionUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'X-MSTR-AuthToken': authToken,
            Prefer: 'respond-async',
            'X-MSTR-ProjectID': projectId,
          },
          credentials: 'include', // Including cookie
          mode: 'cors', // Setting as cors mode for cross origin
        });
        await checkApiStatus(response);
        const botInfo = await response.json();
        return botInfo;
      } catch (err) {
        console.error(err);
      }
    },

    async getAuthToken() {
      // here we use standard login by default, we can also use the other login in methods (SAML/OIDC ...).
      return fetch( serverUrl + '/api/auth/login', {
        method: 'POST',
        credentials: 'include', // including cookie
        mode: 'cors', // setting as CORS mode for cross origin
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loginMode: 1,
          username: 'usernamePlaceholder',
          password: 'passwordPlaceholder',
        }),
      })
        .then((response) => {
          if (response && response.ok) {
            return response.headers.get('X-MSTR-authToken');
          }
          throw Error('Failed to fetch auth token.');
        })
        .catch((error) => {
          console.log('Error:', error);
        });
    },
  };

  async function checkApiStatus(response) {
    const { status } = response;
    if (status >= 200 && status < 300) {
      return response;
    } else {
      const json = await response.json();
      throw new Error(
        "API response returns error: " +
        JSON.stringify(json)
      );
    }
  }

  // get the suggestions and render them
  async function processSuggestions(authToken, serverUrl) {
    const suggestionItems = await API.getSuggestions(authToken, serverUrl);
    const newElement = document.getElementById('suggestions-title');
    newElement.innerText = 'Related Suggestions:';
    // render the suggestion items
    const suggestionHolder = document.getElementById('suggestionHolder');
    suggestionHolder.innerHTML = '';
    suggestionHolder.className = 'suggestions-disabled';
    suggestionItems.suggestions.forEach((suggestion) => {
      // create a new element
      const suggestionElement = document.createElement('div');
      suggestionElement.classList.add('suggestions-item');
      suggestionElement.textContent = suggestion.text;
      // make the suggestions unclickable
      suggestionElement.className = 'suggestions-item-disabled';
      suggestionHolder.appendChild(suggestionElement);
      // when the suggestion is clicked, answer the question
      suggestionElement.addEventListener('click', () => {
        questionPromise = processQuestion(suggestion.text);
      });
    });
    // adjust the height of chatbox when the suggestions are rendered 
    var chatContainer = document.getElementById("chat-container");
    chatContainer.scrollTop = chatContainer.scrollHeight;
    // enable the suggestion items only after the question is answered
    if (questionPromise) {
      await questionPromise;
    }
    suggestionHolder.className = 'suggestions';
    const newItems = Array.from(suggestionHolder.children);
    newItems.forEach((item) => {
      item.className = 'suggestions-item';
    });
  }

  // entry point to process the question asked
  async function processQuestion(message) {
    // disable the suggestion items and input box
    document.getElementById('userInput').disabled = true;
    const suggestionHolder = document.getElementById('suggestionHolder');
    const items = Array.from(suggestionHolder.children);
    items.forEach((item) => {
      item.className = 'suggestion-animation';
      item.textContent = '';
    });
    try {
      addQuestionMsg(message);
      console.log('Searching for bot to answer this question...');
      await processAnswer({ status: QUESTION_STATUS.BOT_ROUTING });
      const answerId = await API.postQuestion(message);
      // can not find an appropriate bot to answer the question
      if (!answerId) {
        // render the answer messgae that no bot is found
        await processAnswer({ status: QUESTION_STATUS.BOT_NOT_FOUND });
        processSuggestions(authToken, serverUrl);
      } else {
        // retrieve the answer content and render it
        botInfo = parseBotConfigs(answerId);
        // get the bot configuration from the bot id
        let botConfig = await API.getBotConfig(botInfo);
        console.log('routing to bot:' + JSON.stringify(botConfig.name));
        // render the answer message of the routed bot info
        await processAnswer({
          status: QUESTION_STATUS.BOT_FOUND,
          botConfig: { name: botConfig.name, id: botInfo.objectId },
        });
        // get the new suggestions when the bot is routed
        processSuggestions(authToken, serverUrl);
        // retrieve the answer content from the answerId
        let answerData = await API.getQuestionAnswer(answerId);
        await processAnswer({ status: QUESTION_STATUS.THINKING, question: answerData.content });
        // wait for the answer to be ready
        while (answerData.status !== 200) {
          console.log('answer is not ready yet, please wait...');
          answerData = await API.getQuestionAnswer(answerId);
          // wait 1 sec
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        await processAnswer({ status: QUESTION_STATUS.ANSWER_READY, question: answerData.content });
      }
    } catch (error) {
      console.error('Error on processing question:', error);
    } finally {
      // enable the suggestion items and input box
      document.getElementById('userInput').disabled = false;
    }
  }
  `,
};

const updateDuplicateCode = (code) => {
  let updatedCode = code;
  Object.keys(duplicateCodeMap).forEach((key) => {
    updatedCode = updatedCode.replace(key, duplicateCodeMap[key]);
  });
  return updatedCode;
};

const createDefaultTemplate = async (relativePath) => {
  let locationEndWithSlash = '';
  if (window.location && window.location.href && typeof window.location.href === 'string') {
    locationEndWithSlash = window.location.href.endsWith('/')
      ? window.location.href
      : `${window.location.href}/`;
  }
  const absolutePath = locationEndWithSlash + relativePath;
  const response = await fetch(absolutePath);
  const template = await response.text();
  return updateDuplicateCode(template);
};

// Object containing all the template objects
// Name ids: 'G{Number}'. Using G (Gallery) prefix for possible future custom examples. Last number is 24 use 1 above it. <--- UPDATE NUMBER HERE AFTER CHANGES
// The defaultUrl is in format: (baseServerUrl + '/' + libraryName + '/app/' + projectId + '/' + dossierId +'/' + pageKey) with pageKey being optional
export const exampleTemplates = {
  dashboard: {
    embeddingType: EmbeddingType.Dashboard,
    content: {
      general: {
        groupName: 'General',
        groupDesc:
          'This group contains examples that demonstrate basic functionality and features of the Embedding SDK.',
        items: {
          simpleExample: {
            id: 'g1',
            desc: 'This example demonstrates how to embed a simple dashboard. It shows how to pass the necessary configuration parameters and embed the dashboard using the Embedding SDK. The example code can be used as a starting point to embed other dossiers.',
            image: './templates/images/basicTemplate.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/27D332AC6D43352E0928B9A1FCAF4AB0',
            },
            name: 'Simple Dashboard Example',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/basicTemplate.html');
            },
          },
          miniExample: {
            id: 'g2',
            desc: 'This example demonstrates the minimum set of properties required to embed a dashboard.',
            image: './templates/images/miniTemplate.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/27D332AC6D43352E0928B9A1FCAF4AB0',
            },
            name: 'Minimal Template',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/miniTemplate.html');
            },
          },

          responsiveHeightExample: {
            id: 'g3',
            desc: 'This example demonstrates how to set up the embedded dashboard to automatically adjust its height based on its content.',
            image: './templates/images/basicTemplateResponsiveHeight.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/27D332AC6D43352E0928B9A1FCAF4AB0',
            },
            name: 'Responsive Height',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/basicTemplateResponsiveHeight.html');
            },
          },
          fullScreenExample: {
            id: 'g4',
            desc: 'This example demonstrates how to set up an embedded dashboard in full screen.',
            image: './templates/images/fullscreenExample.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/27D332AC6D43352E0928B9A1FCAF4AB0',
            },
            name: 'Dashboard in Full Screen',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/fullscreenExample.html');
            },
          },
          interDossierExample: {
            id: 'g5',
            desc: 'This example demonstrates how to use one dashboard to interact with and modify another dashboard.',
            image: './templates/images/interDossierInteraction.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/01EFAD50F648958F0B0575919689E155',
            },
            name: 'Inter-dashboard Interaction',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/interDossierInteraction.html');
            },
          },
          createFromInstanceExample: {
            id: 'g27',
            desc: 'This example demonstrates how to embed a dashboard from an instance.',
            image: './templates/images/DashboardInstance.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/01EFAD50F648958F0B0575919689E155',
            },
            name: 'Embed Dashboard From Instance',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/createFromInstance.html');
            },
          },
        },
      },
      authentication: {
        groupName: 'Authentication',
        groupDesc:
          'This group contains examples that demonstrate how to authenticate an embedded dashboard.',
        items: {
          authenticationExample: {
            id: 'g17',
            desc: 'This example demonstrates how to embed a dashboard with authentication modes of OIDC/SAML/Standard/Guest.',
            image: './templates/images/standardAuth.png',
            defaultEnvInfo: {
              defaultUrl: 'https://demo.microstrategy.com/MicroStrategyLibrary',
            },
            name: 'All Authentication Modes',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/authentication/standardAuth.html');
            },
          },
        },
      },
      filter: {
        groupName: 'Filters',
        groupDesc:
          'This group contains examples that demonstrate how to use various types of filters in an embedded dashboard, including attribute and metric filters. Filters are used to refine the data displayed to users.',
        items: {
          attributeSelectorFilterExample: {
            id: 'g6',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has an attribute selector filter. Attribute selector filters allow users to select attribute values to include or exclude from the dashboard.',
            image: './templates/images/attributeSelector.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/58FD451E1541F23210E9698F84A71985/K149--K142',
            },
            name: 'Attribute Selector',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/filterTemplates/attributeSelector.html');
            },
          },
          attributSearchSelectorFilterExample: {
            id: 'g7',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has an attribute search selector filter. The attribute search selector allows users to use a search bar to find and filter attribute values.',
            image: './templates/images/attributeSearchSelector.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/58FD451E1541F23210E9698F84A71985/K125--K98',
            },
            name: 'Attribute Search',
            defaultTemplate: () => {
              return createDefaultTemplate(
                './templates/filterTemplates/attributeSearchSelector.html',
              );
            },
          },
          attributeSliderFilterExample: {
            id: 'g8',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has an attribute slider filter. The attribute slider filter allows users to filter attribute values using a slider.',
            image: './templates/images/attributeSlider.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/58FD451E1541F23210E9698F84A71985/K213--K206',
            },
            name: 'Attribute Slider',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/filterTemplates/attributeSlider.html');
            },
          },
          calendarFilterExample: {
            id: 'g9',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has a calendar filter. The calendar filter allows users to filter based on dates on the calendar.',
            image: './templates/images/calendar.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/58FD451E1541F23210E9698F84A71985/K213--K206',
            },
            name: 'Calendar',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/filterTemplates/calendar.html');
            },
          },
          metricQualByRankFilterExample: {
            id: 'g10',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has a metric qualification by rank filter. The metric qualification by rank filter allows users to qualify metrics based on their rank.',
            image: './templates/images/metricQualByRank.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/58FD451E1541F23210E9698F84A71985/K359--K352',
            },
            name: 'Metric Qual By Rank',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/filterTemplates/metricQualByRank.html');
            },
          },
          metricQualByValueFilterExample: {
            id: 'g11',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has a metric qualification by value filter. The metric qualification by value filter allows users to qualify metrics based on their value.',
            image: './templates/images/metricQualByValue.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/58FD451E1541F23210E9698F84A71985/K314--K307',
            },
            name: 'Metric Qual By Value',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/filterTemplates/metricQualByValue.html');
            },
          },
          metricSliderByRankFilterExample: {
            id: 'g12',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has a metric slider by rank filter. The metric slider by rank filter allows users to use a slider to filter metrics based on their rank.',
            image: './templates/images/metricSliderByRank.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/58FD451E1541F23210E9698F84A71985/K404--K397',
            },
            name: 'Metric Slider By Rank',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/filterTemplates/metricSliderByRank.html');
            },
          },
          metricSliderByValueFilterExample: {
            id: 'g13',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has a metric slider by value filter. The metric slider by value filter allows users to use a slider to filter metrics based on their value.',
            image: './templates/images/metricSliderByValue.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/58FD451E1541F23210E9698F84A71985/K449--K442',
            },
            name: 'Metric Slider By Value',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/filterTemplates/metricSliderByValue.html');
            },
          },
        },
      },
      navigation: {
        groupName: 'Navigation',
        groupDesc:
          'This group contains examples that demonstrate how to use Embedding SDK to navigate embedded content.',
        items: {
          pageNavigationExample: {
            id: 'g14',
            desc: 'This example showcases how to switch pages and navigate an embedded dashboard.',
            image: './templates/images/navigationTemplate.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/27D332AC6D43352E0928B9A1FCAF4AB0',
            },
            name: 'Page Navigation',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/navigationTemplate.html');
            },
          },
        },
      },
      visualization: {
        groupName: EmbeddingType.Visualization,
        groupDesc:
          'This group contains examples that demonstrate capabilities for visualizations through Embedding SDK.',
        items: {
          singleVizExample: {
            id: 'g15',
            desc: 'This example demonstrates how to display a single visualization from a dashboard page that contains multiple visualizations, as well as how to manipulate the displayed visualization.',
            image: './templates/images/singleVisualizationTemplate.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/2A89AE45B6470A22489C6C94113AA1DF/K53--K46',
            },
            name: 'Single Visualization',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/singleVisualizationTemplate.html');
            },
          },
          visualizationSelectorExample: {
            id: 'g19',
            desc: 'This example demonstrates how to select elements from visualizations.',
            image: './templates/images/VizSelection.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/F4A1674A6A45179F43CED88258B0271C',
            },
            name: 'Visualization Element Selection',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/visualization/visualizationSelection.html');
            },
          },
        },
      },
      authoring: {
        groupName: 'Authoring',
        groupDesc:
          'This group contains examples that demonstrates authoring capabilities available using Embedding SDK. ',
        items: {
          authoringExample: {
            id: 'g16',
            desc: 'This example showcases how to author an embedded dashboard.',
            image: './templates/images/authoring.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/27D332AC6D43352E0928B9A1FCAF4AB0',
            },
            name: 'Authoring',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/authoring/authoring.html');
            },
          },
        },
      },
      exporting: {
        groupName: 'Exporting',
        groupDesc:
          'This group contains examples for exporting data or visualizations for the embedded content.',
        items: {
          exportToExcelOrPDFExample: {
            id: 'g18',
            desc: 'This example showcases how to export to excel or pdf an embedded dashboard.',
            image: './templates/images/exportToExcelOrPDF.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/27D332AC6D43352E0928B9A1FCAF4AB0',
            },
            name: 'Export to Excel or PDF',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/exporting/exportToExcelOrPDF.html');
            },
          },
        },
      },
      prompts: {
        groupName: 'Prompts',
        groupDesc:
          'This group contains examples for utilizing prompts in the embedded dashboard. Prompts are used to dynamically modify the behavior of a dashboard at runtime.',
        items: {
          attributeElementPromptExample: {
            id: 'g20',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has an attribute element prompt.',
            image: './templates/images/attributeElementsPrompt.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/3CB9254F9C4E5AED52C14FB1DAADF629',
            },
            name: 'Attribute Element',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/prompts/attributeElementsPrompt.html');
            },
          },
          attributeQualificationPromptExample: {
            id: 'g21',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has an attribute qualification prompt.',
            image: './templates/images/attributeQualificationPrompt.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/F4A1674A6A45179F43CED88258B0271C',
            },
            name: 'Attribute Qualification',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/prompts/attributeQualificationPrompt.html');
            },
          },
          metricQualificationPromptExample: {
            id: 'g22',
            desc: 'This example demonstrates how to manipulate an embedded dashboard that has a metric qualification prompt.',
            image: './templates/images/metricQualificationPrompt.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/B7CA92F04B9FAE8D941C3E9B7E0CD754/F4A1674A6A45179F43CED88258B0271C',
            },
            name: 'Metric Qualification',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/prompts/metricQualificationPrompt.html');
            },
          },
        },
      },
      panelStacks: {
        groupName: 'Panel Stacks',
        groupDesc:
          'This group contains examples that demonstrate how utilize panel stacks within an embeded dashboard. Panel stacks are dynamic containers that allow you to add, remove, and rearrange individual panels on a dashboard.',
        items: {
          interactWithPanelStacksExample: {
            id: 'g23',
            desc: 'This example showcases panel stack interactions on an embedded dashboard.',
            image: './templates/images/interactWithPanelStacks.png',
            defaultEnvInfo: {
              defaultUrl:
                'https://demo.microstrategy.com/MicroStrategyLibrary/app/EC70648611E7A2F962E90080EFD58751/5587CB0B11EB8297835E0080AFEB08B5',
            },
            name: 'Interact with Panel Stacks',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/panelStacks/interactWithPanelStacks.html');
            },
          },
        },
      },
    },
  },
  library: {
    embeddingType: EmbeddingType.Library,
    content: {
      general: {
        groupName: 'Library Page',
        groupDesc:
          'This group contains examples that demonstrate how to customize and embed the Library home page. The Library home page is a centralized location where users can store and manage their reports, documents, dossiers, and other analytical objects.',
        items: {
          simpleExample: {
            id: 'g24',
            desc: 'This example showcases an embedded Library page.',
            image: './templates/images/embedLibraryPage.png',
            defaultEnvInfo: {
              defaultUrl: 'https://demo.microstrategy.com/MicroStrategyLibrary',
            },
            name: 'Embed Library Page',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/libraryPage/embedLibraryPage.html');
            },
          },
        },
      },
    },
  },
  bot: {
    embeddingType: EmbeddingType.Bot,
    content: {
      general: {
        groupName: EmbeddingType.Bot,
        groupDesc:
          'This group contains examples that demonstrate how to customize and embed the bot page',
        items: {
          simpleExample: {
            id: 'g25',
            subType: EmbeddingType.Bot,
            desc: 'This example showcases an embedded bot page.',
            image: './templates/images/BotTemplate.png',
            // TBD: add default bot url from demo page
            defaultEnvInfo: {
              defaultUrl: 'https://demo.microstrategy.com/MicroStrategyLibrary',
              projectId: '38328E048D427571975C388F7C402AD8',
              objectId: 'A81721DEC24012C1C40522B8A8EC36DE',
              customApplicationId: 'C2B2023642F6753A2EF159A75E0CFF29',
            },
            name: 'Embed Bot Page',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/bot/embeddBot.html');
            },
          },
          botApiDemo: {
            id: 'g26',
            subType: EmbeddingType.Bot_Api,
            desc: 'This example showcases how to embed a customized bot from chat APIs.',
            image: './templates/images/botApi.png',
            // TBD: add default bot url from demo page
            defaultEnvInfo: {
              defaultUrl: 'https://demo.microstrategy.com/MicroStrategyLibrary',
              projectId: '38328E048D427571975C388F7C402AD8',
              objectId: 'EEA9BF707B4288E7369D0B9D5D02E6A5',
              username: 'usernamePlaceholder',
              password: 'passwordPlaceholder',
              customApplicationId: 'C2B2023642F6753A2EF159A75E0CFF29',
            },
            name: 'Embed Single Bot from APIs',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/bot/embedBotApi.html');
            },
          },
          unifiedBotApiDemo: {
            id: 'g29',
            subType: EmbeddingType.Bot_Auto,
            desc: 'This example showcases how to embed a customized bot from unified chat APIs. For each question asked, it searches for the most suitable bot to answer this question. This example renders the visualizations by native-embedding-sdk.',
            image: './templates/images/AutoBot.png',
            // TBD: add default bot url from demo page
            defaultEnvInfo: {
              defaultUrl: 'https://demo.microstrategy.com/MicroStrategyLibrary',
              username: 'usernamePlaceholder',
              password: 'passwordPlaceholder',
              customApplicationId: 'C2B2023642F6753A2EF159A75E0CFF29',
              /* eslint-disable no-template-curly-in-string */
              // The request phrases that are needed for adjustments
              requestPhrases: [
                `const askQuestionUrl = serverUrl + '/api/questions'`,
                `const getSuggestionsUrl = serverUrl + '/api/questions/suggestions'`,
              ],
            },
            name: 'Embed Auto Bot from APIs with Native SDK',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/bot/embedUnifiedBot.html');
            },
          },
          unifiedBotApiDemoWithImage: {
            id: 'g30',
            subType: EmbeddingType.Bot_AutoImage,
            desc: 'This example showcases how to embed a customized bot from unified chat APIs. For each question asked, it searches for the most suitable bot to answer this question. This example renders the visualizations by images.',
            // TODO: will change the image after UX is ready
            image: './templates/images/AutoBot.png',
            // TBD: add default bot url from demo page
            defaultEnvInfo: {
              defaultUrl: 'https://demo.microstrategy.com/MicroStrategyLibrary',
              username: 'usernamePlaceholder',
              password: 'passwordPlaceholder',
              customApplicationId: 'C2B2023642F6753A2EF159A75E0CFF29',
              /* eslint-disable no-template-curly-in-string */
              // The request phrases that are needed for adjustments
              requestPhrases: [
                `const askQuestionUrl = serverUrl + '/api/questions'`,
                `const getSuggestionsUrl = serverUrl + '/api/questions/suggestions'`,
              ],
            },
            name: 'Embed Auto Bot from APIs with Image',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/bot/embedUnifiedBotWithImage.html');
            },
          },
          nextGenAiDemo: {
            id: 'g31',
            subType: EmbeddingType.NextGet_AI,
            desc: 'This example showcases how to embed a Next-Gen AI Bot from APIs.',
            image: './templates/images/botApi.png',
            // TBD: add default bot url from demo page
            defaultEnvInfo: {
              defaultUrl: 'https://demo.microstrategy.com/MicroStrategyLibrary',
              projectId: '38328E048D427571975C388F7C402AD8',
              objectId: 'EEA9BF707B4288E7369D0B9D5D02E6A5',
              username: 'usernamePlaceholder',
              password: 'passwordPlaceholder',
              customApplicationId: 'C2B2023642F6753A2EF159A75E0CFF29',
            },
            name: 'Embed Next-Gen AI Bot from APIs',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/bot/embedNextGenAI.html');
            },
          },
        },
      },
    },
  },
  visualization: {
    embeddingType: EmbeddingType.Visualization,
    content: {
      general: {
        groupName: EmbeddingType.Visualization,
        groupDesc:
          'This group contains examples that demonstrate how to customize and embed the visulizations',
        items: {
          simpleExample: {
            id: 'g28',
            desc: 'This example showcases how to embed visulizations from a given dashboard.',
            image: './templates/images/nativeViz.png',
            defaultEnvInfo: {
              defaultUrl: 'https://demo.microstrategy.com/MicroStrategyLibrary',
              projectId: 'EC70648611E7A2F962E90080EFD58751',
              objectId: '85D9C39DC546D9E07FB19FBF36F13A14',
            },
            name: 'Embed Visualizations From Dashboard',
            defaultTemplate: () => {
              return createDefaultTemplate('./templates/visualization/basicTemplate.html');
            },
          },
        },
      },
    },
  },
};

// Function that generates the initial code for the template
export const templateInitialCode = (
  embeddingType = EmbeddingType.Dashboard,
  exampleName = exampleTemplates.dashboard.content.general.items.simpleExample.name,
  exampleGroupName = exampleTemplates.dashboard.content.general.groupName,
) => {
  let initialCode = '';

  // Loop through the exampleTemplates to find the specified template based on exampleGroupName and exampleName
  // then get the initial code for that template
  Object.values(
    Object.values(exampleTemplates).find((example) => example.embeddingType === embeddingType)
      .content,
  ).forEach((exampleGroup) => {
    if (exampleGroup.groupName === exampleGroupName && exampleGroup.items) {
      Object.values(exampleGroup.items).forEach((example) => {
        if (example.name === exampleName) {
          // Use the defaultUrl in template used for demos
          initialCode = example.defaultTemplate();
        }
      });
    }
  });

  return initialCode;
};

export const embeddingTypeToExampleMap = {
  [EmbeddingType.Dashboard]: exampleTemplates.dashboard.content.general.items.simpleExample,
  [EmbeddingType.Library]: exampleTemplates.library.content.general.items.simpleExample,
  [EmbeddingType.Visualization]: exampleTemplates.visualization.content.general.items.simpleExample,
  [EmbeddingType.Bot]: exampleTemplates.bot.content.general.items.simpleExample,
  [EmbeddingType.Bot_Api]: exampleTemplates.bot.content.general.items.botApiDemo,
  [EmbeddingType.Bot_Auto]: exampleTemplates.bot.content.general.items.unifiedBotApiDemo,
  [EmbeddingType.Bot_AutoImage]:
    exampleTemplates.bot.content.general.items.unifiedBotApiDemoWithImage,
  [EmbeddingType.NextGet_AI]: exampleTemplates.bot.content.general.items.nextGenAiDemo,
};

// Function that generates the initial code for the template
// TODO: add viz type handling
export const updateDefaultTemplateForEmbeddingType = async (
  embeddingType,
  envSettingInfo = null,
) => {
  let template;
  let templateStr = '';

  if (embeddingType in embeddingTypeToExampleMap) {
    template = embeddingTypeToExampleMap[embeddingType];
    templateStr = await template.defaultTemplate();
  } else {
    throw new Error('Invalid embedding type.');
  }
  if (envSettingInfo != null) {
    templateStr = populateEnvInfo(
      templateStr,
      embeddingType,
      envSettingInfo,
      template.defaultEnvInfo,
    );
  }

  return templateStr;
};
